// Static demo-mode fill-in data for ProdaffTasksTab.component.jsx.
// Shapes must match exactly what this view's own rendering expects:
// - DEMO_PROJECTS mirrors `state.team.projects` — [{ id, data: { project_name,
//   deleted } }] — the shape `projectOptions` and the mount effect's
//   `projects` state both map over.
// - DEMO_MEMBER_DETAILS mirrors useTeamMemberDetailsForSelect's `memberDetails`
//   — a uid-keyed map of { userName, userEmailid } — read by `members`
//   (useMemo) and `assigneeBodyTemplate` (rowData.Assignee -> memberDetails[uid]).
// - DEMO_TASKS mirrors a `taskDatabase` doc — { id, Title, Description,
//   Status, Priority, Assignee, ProjectID, ETA, Deleted } — Status must be one
//   of STATUS_OPTIONS/STATUS_SEVERITY, Priority one of PRIORITY_OPTIONS,
//   Assignee a uid present in DEMO_MEMBER_DETAILS, ProjectID an id present in
//   DEMO_PROJECTS, ETA a "MM/DD/YYYY" string (etaBodyTemplate splits on '/').
//
// Assignee ids/names/emails match the canonical roster in
// EmployeeList.demoData.js. ETAs are computed relative to "today" — future
// for anything not yet Done, past for completed tasks — so the board never
// shows an overdue-looking "TO DO"/"IN PROGRESS" task no matter when the
// demo is opened (see UserAttendance.demoData.js for the same pattern).

const DAY_MS = 24 * 60 * 60 * 1000;
const etaAt = (daysOffset) => {
  const d = new Date(Date.now() + daysOffset * DAY_MS);
  const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(d.getUTCDate()).padStart(2, '0');
  return `${mm}/${dd}/${d.getUTCFullYear()}`;
};

export const DEMO_PROJECTS = [
  {
    id: 'demo-project-website-redesign',
    data: { project_name: 'Website Redesign', deleted: false },
  },
  {
    id: 'demo-project-mobile-app',
    data: { project_name: 'Mobile App', deleted: false },
  },
  {
    id: 'demo-project-internal-tools',
    data: { project_name: 'Internal Tools', deleted: false },
  },
  {
    id: 'demo-project-marketing-site',
    data: { project_name: 'Marketing Website', deleted: false },
  },
];

export const DEMO_MEMBER_DETAILS = {
  'demo-user': {
    userName: 'Ryan Mitchell',
    userEmailid: 'ryan.mitchell@meridianlabs.com',
  },
  'demo-user-2': {
    userName: 'Rachel Turner',
    userEmailid: 'rachel.turner@meridianlabs.com',
  },
  'demo-user-3': {
    userName: 'Michael Chen',
    userEmailid: 'michael.chen@meridianlabs.com',
  },
};

export const DEMO_TASKS = [
  {
    id: 'demo-task-1',
    Title: 'Homepage layout',
    Description: 'Rebuild the homepage hero and feature grid to match Figma.',
    Status: 'DONE',
    Priority: 'P1',
    Assignee: 'demo-user',
    ProjectID: 'demo-project-website-redesign',
    ETA: etaAt(-5),
    Deleted: false,
  },
  {
    id: 'demo-task-2',
    Title: 'Client feedback review',
    Description: 'Go through the client’s round-2 feedback and triage changes.',
    Status: 'IN PROGRESS',
    Priority: 'P2',
    Assignee: 'demo-user-2',
    ProjectID: 'demo-project-website-redesign',
    ETA: etaAt(3),
    Deleted: false,
  },
  {
    id: 'demo-task-3',
    Title: 'Responsive QA pass',
    Description:
      'Verify layouts across mobile, tablet, and desktop breakpoints.',
    Status: 'TO DO',
    Priority: 'P3',
    Assignee: 'demo-user-3',
    ProjectID: 'demo-project-website-redesign',
    ETA: etaAt(10),
    Deleted: false,
  },
  {
    id: 'demo-task-4',
    Title: 'API integration',
    Description: 'Wire up the mobile app to the new v2 REST endpoints.',
    Status: 'IN PROGRESS',
    Priority: 'P1',
    Assignee: 'demo-user',
    ProjectID: 'demo-project-mobile-app',
    ETA: etaAt(5),
    Deleted: false,
  },
  {
    id: 'demo-task-5',
    Title: 'Bug fixes',
    Description: 'Fix crash on login for users with expired sessions.',
    Status: 'QA',
    Priority: 'P1',
    Assignee: 'demo-user-3',
    ProjectID: 'demo-project-mobile-app',
    ETA: etaAt(2),
    Deleted: false,
  },
  {
    id: 'demo-task-6',
    Title: 'Push notification setup',
    Description: 'Add FCM push notifications for task assignment updates.',
    Status: 'TO DO',
    Priority: 'P3',
    Assignee: 'demo-user-2',
    ProjectID: 'demo-project-mobile-app',
    ETA: etaAt(20),
    Deleted: false,
  },
  {
    id: 'demo-task-7',
    Title: 'Dashboard analytics',
    Description:
      'Add weekly productivity trend chart to the internal dashboard.',
    Status: 'DONE',
    Priority: 'P2',
    Assignee: 'demo-user',
    ProjectID: 'demo-project-internal-tools',
    ETA: etaAt(-12),
    Deleted: false,
  },
  {
    id: 'demo-task-8',
    Title: 'Code review',
    Description: 'Review the pending PRs on the reporting service refactor.',
    Status: 'IN PROGRESS',
    Priority: 'P4',
    Assignee: 'demo-user-2',
    ProjectID: 'demo-project-internal-tools',
    ETA: etaAt(1),
    Deleted: false,
  },
  {
    id: 'demo-task-9',
    Title: 'CI pipeline cleanup',
    Description: 'Speed up the build pipeline and remove flaky test steps.',
    Status: 'TO DO',
    Priority: 'P3',
    Assignee: 'demo-user-3',
    ProjectID: 'demo-project-internal-tools',
    ETA: etaAt(15),
    Deleted: false,
  },
  {
    id: 'demo-task-10',
    Title: 'Landing page copy',
    Description: 'Write updated copy for the pricing and features sections.',
    Status: 'QA',
    Priority: 'P2',
    Assignee: 'demo-user',
    ProjectID: 'demo-project-marketing-site',
    ETA: etaAt(3),
    Deleted: false,
  },
  {
    id: 'demo-task-11',
    Title: 'SEO audit',
    Description: 'Run a full technical SEO audit and log fixes needed.',
    Status: 'TO DO',
    Priority: 'P4',
    Assignee: 'demo-user-2',
    ProjectID: 'demo-project-marketing-site',
    ETA: etaAt(25),
    Deleted: false,
  },
  {
    id: 'demo-task-12',
    Title: 'Blog redesign',
    Description: 'Apply the new brand system to the blog template.',
    Status: 'DONE',
    Priority: 'P3',
    Assignee: 'demo-user-3',
    ProjectID: 'demo-project-marketing-site',
    ETA: etaAt(-20),
    Deleted: false,
  },
];
